import React, { useEffect, useMemo, useRef } from "react";
import { Animated, Easing, Platform } from "react-native";
import { SvgXml } from "react-native-svg";
import { colors } from "../theme";

export type MascotMood =
  | "happy"
  | "wave"
  | "cheer"
  | "curious"
  | "sleep"
  | "sad";
export type MascotAccessory = "hat" | "backpack" | "cape" | "crown";

type MascotProps = {
  size?: number;
  color?: string;
  mood?: MascotMood;
  accessory?: MascotAccessory;
  sparkles?: boolean;
  bob?: boolean; // gentle vertical idle bounce
};

// Build the SVG body from mood / accessory primitives.
// Mirrors mascot.jsx from the Claude Design bundle.
function buildMascotSvg({
  color,
  mood,
  accessory,
  sparkles,
}: Required<Pick<MascotProps, "color" | "mood">> & {
  accessory?: MascotAccessory;
  sparkles?: boolean;
}): string {
  const ink = "#2E2316";
  const cheek = colors.cheek;

  const armLeft = `<ellipse cx="34" cy="118" rx="14" ry="20" fill="${color}"${
    mood === "cheer" ? ' transform="rotate(-30 34 118)"' : ""
  }/>`;
  const armRight = `<ellipse cx="166" cy="118" rx="14" ry="20" fill="${color}"${
    mood === "cheer" || mood === "wave" ? ' transform="rotate(30 166 118)"' : ""
  }/>`;

  const body = `<path d="M40 92 C 40 50, 70 28, 100 28 C 130 28, 160 50, 160 92 C 160 142, 134 168, 100 168 C 66 168, 40 142, 40 92 Z" fill="${color}"/>`;
  const belly = `<ellipse cx="100" cy="118" rx="42" ry="34" fill="#fff" opacity="0.18"/>`;
  const cheeks = `<ellipse cx="70" cy="115" rx="9" ry="5" fill="${cheek}"/><ellipse cx="130" cy="115" rx="9" ry="5" fill="${cheek}"/>`;

  let eyes = "";
  if (mood === "sleep") {
    eyes =
      `<path d="M70 92 q8 7 16 0" stroke="${ink}" stroke-width="4" stroke-linecap="round" fill="none"/>` +
      `<path d="M114 92 q8 7 16 0" stroke="${ink}" stroke-width="4" stroke-linecap="round" fill="none"/>`;
  } else if (mood === "sad") {
    eyes =
      `<circle cx="78" cy="93" r="6" fill="${ink}"/>` +
      `<circle cx="122" cy="93" r="6" fill="${ink}"/>`;
  } else {
    eyes =
      `<ellipse cx="78" cy="92" rx="7" ry="9" fill="${ink}"/>` +
      `<ellipse cx="122" cy="92" rx="7" ry="9" fill="${ink}"/>` +
      `<circle cx="80" cy="89" r="2.4" fill="#fff"/>` +
      `<circle cx="124" cy="89" r="2.4" fill="#fff"/>`;
  }

  let mouth = "";
  if (mood === "sleep") {
    mouth = `<path d="M92 122 q8 4 16 0" stroke="${ink}" stroke-width="3.5" stroke-linecap="round" fill="none"/>`;
  } else if (mood === "sad") {
    mouth = `<path d="M84 130 q16 -10 32 0" stroke="${ink}" stroke-width="4" stroke-linecap="round" fill="none"/>`;
  } else if (mood === "cheer") {
    mouth = `<path d="M82 116 q18 28 36 0 q-18 8 -36 0 Z" fill="${ink}"/>`;
  } else if (mood === "curious") {
    mouth = `<ellipse cx="100" cy="124" rx="5" ry="6" fill="${ink}"/>`;
  } else {
    mouth = `<path d="M82 118 q18 22 36 0" stroke="${ink}" stroke-width="4.5" stroke-linecap="round" fill="none"/>`;
  }

  const feet =
    `<ellipse cx="80" cy="166" rx="14" ry="6" fill="${color}"/>` +
    `<ellipse cx="120" cy="166" rx="14" ry="6" fill="${color}"/>`;

  const zs =
    mood === "sleep"
      ? `<g fill="${ink}" opacity="0.7" font-family="sans-serif" font-weight="800">` +
        `<text x="156" y="50" font-size="20">z</text>` +
        `<text x="172" y="34" font-size="14">z</text>` +
        `</g>`
      : "";

  const showSparkles = sparkles || mood === "cheer";
  const sparkleSvg = showSparkles
    ? `<g fill="${colors.star}">` +
      `<path d="M30 40 l3 7 7 3 -7 3 -3 7 -3 -7 -7 -3 7 -3 z"/>` +
      `<path d="M178 60 l2 5 5 2 -5 2 -2 5 -2 -5 -5 -2 5 -2 z"/>` +
      `<path d="M16 96 l2 4 4 2 -4 2 -2 4 -2 -4 -4 -2 4 -2 z"/>` +
      `</g>`
    : "";

  let acc = "";
  if (accessory === "hat") {
    acc =
      `<g>` +
      `<ellipse cx="100" cy="42" rx="36" ry="6" fill="#2E2316"/>` +
      `<path d="M70 42 L80 16 L120 16 L130 42 Z" fill="#5B8DEF"/>` +
      `<rect x="82" y="30" width="36" height="6" fill="#FFD86B"/>` +
      `</g>`;
  } else if (accessory === "crown") {
    acc =
      `<g>` +
      `<path d="M70 38 L80 18 L92 32 L100 14 L108 32 L120 18 L130 38 Z" fill="#FFD86B" stroke="#B45A24" stroke-width="2" stroke-linejoin="round"/>` +
      `<circle cx="100" cy="30" r="3" fill="#E85A8C"/>` +
      `</g>`;
  } else if (accessory === "backpack") {
    acc =
      `<g>` +
      `<rect x="58" y="82" width="24" height="30" rx="4" fill="#27B59A"/>` +
      `<rect x="60" y="88" width="20" height="4" fill="#1F8A78"/>` +
      `</g>`;
  } else if (accessory === "cape") {
    acc = `<path d="M40 96 Q20 130 40 168 L60 144 Z" fill="#E85A8C"/>`;
  }

  const shadow = `<ellipse cx="100" cy="178" rx="58" ry="6" fill="rgba(0,0,0,0.10)"/>`;

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" width="200" height="200">` +
    shadow +
    armLeft +
    armRight +
    body +
    belly +
    cheeks +
    eyes +
    mouth +
    feet +
    zs +
    sparkleSvg +
    acc +
    `</svg>`
  );
}

export function Mascot({
  size = 120,
  color = colors.primary,
  mood = "happy",
  accessory,
  sparkles,
  bob = false,
}: MascotProps) {
  const offset = useRef(new Animated.Value(0)).current;

  const svg = useMemo(
    () => buildMascotSvg({ color, mood, accessory, sparkles }),
    [color, mood, accessory, sparkles],
  );

  // Web/RN-Web's <Image> renders SVG data URIs natively; the native
  // Android/iOS <Image> does not, so we render via react-native-svg there.
  const uri = useMemo(
    () => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`,
    [svg],
  );

  useEffect(() => {
    if (!bob) return;
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(offset, {
          toValue: -6,
          duration: 1200,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
        Animated.timing(offset, {
          toValue: 0,
          duration: 1200,
          easing: Easing.inOut(Easing.ease),
          useNativeDriver: true,
        }),
      ]),
    );
    animation.start();
    return () => animation.stop();
  }, [bob, offset]);

  if (Platform.OS === "web") {
    return (
      <Animated.Image
        source={{ uri }}
        style={{
          width: size,
          height: size,
          transform: [{ translateY: offset }],
        }}
        resizeMode="contain"
      />
    );
  }

  return (
    <Animated.View
      style={{
        width: size,
        height: size,
        transform: [{ translateY: offset }],
      }}
    >
      <SvgXml xml={svg} width={size} height={size} />
    </Animated.View>
  );
}

export default Mascot;
